"use strict";
/**
 * Kalshi venue API — every client call that ultimately hits Kalshi.
 *
 * ORDER PATH: placeOrder() → POST /kalshi/orders
 *   → server src/server/routes/trade/ → src/server/kalshi.ts kalshiRequest()
 *   → Kalshi exchange API.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEvents = getEvents;
exports.getMarketsByEvent = getMarketsByEvent;
exports.getMarket = getMarket;
exports.placeOrder = placeOrder;
exports.getOrder = getOrder;
exports.getOrders = getOrders;
exports.cancelOrder = cancelOrder;
exports.getBalance = getBalance;
exports.subscribeKalshiOrderbook = subscribeKalshiOrderbook;
const request_1 = require("./request");
const auth_1 = require("../auth");
// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
/**
 * Fetch events from Kalshi API
 * @param limit - Number of events to fetch
 * @param category - Event category filter
 * @param cursor - Pagination cursor
 * @returns Events data
 */
async function getEvents(limit = 100, category, cursor, withNestedMarkets = false, status = "open", minCloseTs) {
    const params = { limit };
    if (cursor) {
        params.cursor = cursor;
    }
    if (category) {
        params.category = category;
    }
    if (withNestedMarkets) {
        params.with_nested_markets = "true";
    }
    if (status) {
        params.status = status;
    }
    if (minCloseTs) {
        params.min_close_ts = minCloseTs;
    }
    return (0, request_1.apiRequest)("GET", "/events", {
        params,
    });
}
/**
 * Fetch markets for a specific event
 * @param eventTicker - The event ticker to fetch markets for
 * @returns Event detail with all markets for that event
 */
async function getMarketsByEvent(eventTicker) {
    if (!eventTicker) {
        throw new Error("Event ticker is required");
    }
    return (0, request_1.apiRequest)("GET", `/markets/event/${eventTicker}`);
}
// ---------------------------------------------------------------------------
// Markets
// ---------------------------------------------------------------------------
/**
 * Get specific market details
 * @param marketId - Market ID to fetch
 * @returns Market data
 */
async function getMarket(marketId) {
    return (0, request_1.apiRequest)("GET", `/markets/${marketId}`);
}
// ---------------------------------------------------------------------------
// Orders (the latency-critical path)
// ---------------------------------------------------------------------------
/**
 * Place a new order
 * @param orderRequest - Order request data
 * @returns Order response
 */
async function placeOrder(orderRequest) {
    return (0, request_1.apiRequest)("POST", "/kalshi/orders", {
        body: orderRequest,
    });
}
/**
 * Get order by ID
 * @param orderId - Order ID to fetch
 * @returns Order response
 */
async function getOrder(orderId) {
    return (0, request_1.apiRequest)("GET", `/orders/${orderId}`);
}
/**
 * Get user's orders with pagination
 * @param limit - Number of orders to fetch
 * @param cursor - Pagination cursor
 * @returns Orders list with cursor
 */
async function getOrders(limit = 100, cursor) {
    const params = { limit };
    if (cursor) {
        params.cursor = cursor;
    }
    return (0, request_1.apiRequest)("GET", "/portfolio/orders", { params });
}
/**
 * Cancel an order
 * @param orderId - Order ID to cancel
 * @returns Order response
 */
async function cancelOrder(orderId) {
    return (0, request_1.apiRequest)("DELETE", `/orders/${orderId}`);
}
// ---------------------------------------------------------------------------
// Portfolio
// ---------------------------------------------------------------------------
/**
 * Get account balance
 * @returns Account balance
 */
async function getBalance() {
    return (0, request_1.apiRequest)("GET", "/portfolio/balance");
}
// ---------------------------------------------------------------------------
// Orderbook subscription (server-side WS cache)
// ---------------------------------------------------------------------------
/**
 * Subscribe to Kalshi orderbook updates for given market tickers.
 * The server lazily initializes the WebSocket connection and caches
 * snapshots; the client then polls the cached books.
 *
 * @param marketTickers - Kalshi market tickers to subscribe to
 * @throws Error when the request fails or the session is missing
 */
async function subscribeKalshiOrderbook(marketTickers) {
    const sessionToken = (0, auth_1.getSessionToken)();
    if (!sessionToken) {
        throw new Error("Not authenticated. Please login.");
    }
    const response = await fetch(`${(0, request_1.getApiBaseUrl)()}/api/kalshi-orderbook/subscribe`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Session-Token": sessionToken,
        },
        body: JSON.stringify({ marketTickers }),
    });
    if (!response.ok) {
        const errorText = await response.text();
        let errorDetail = response.statusText || "Unknown error";
        try {
            const parsed = JSON.parse(errorText);
            if (parsed?.message) {
                errorDetail = parsed.message;
            }
            else if (parsed?.error) {
                errorDetail = parsed.error;
            }
            else if (errorText) {
                errorDetail = errorText;
            }
        }
        catch (parseError) {
            if (errorText) {
                errorDetail = errorText;
            }
        }
        throw new Error(`Failed to subscribe to Kalshi orderbook: ${errorDetail}`);
    }
}
